import { __assign, __read, __rest } from "tslib";
import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import moment from "moment";
import { CalendarIcon } from "@tencent/tea-icons-react";
import CalendarPart from "../calendar/CalendarPart";
import { useDefaultValue } from "../form/controlled";
import { CalendarTable } from "../calendar/CalendarTable";
import { Input } from "../input/Input";
import { DropdownBox } from "../dropdown";
import { useTranslation } from "../i18n";
import { Popover } from "../popover/Popover";
import { DatePickerTrigger, isAfter, isBefore, isValidMonth } from "./util";
import { useDefault } from "../_util/use-default";
import { useConfig } from "../_util/config-context";
import { Icon } from "../icon";
import { noop } from "../_util/noop";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { mergeEventProps } from "../_util/merge-event-props";
import { KeyMap } from "../_util/key-map";
export var MonthPicker = forwardRefWithStatics(function MonthPicker(props, ref) {
    var t = useTranslation(moment);
    var classPrefix = useConfig().classPrefix;
    var _a = useDefaultValue(props), header = _a.header, className = _a.className, style = _a.style, value = _a.value, onChange = _a.onChange, _b = _a.onInputValueChange, onInputValueChange = _b === void 0 ? noop : _b, disabled = _a.disabled, _c = _a.format, format = _c === void 0 ? "YYYY-MM" : _c, _d = _a.placeholder, placeholder = _d === void 0 ? t.selectDate : _d, _e = _a.defaultOpen, defaultOpen = _e === void 0 ? false : _e, open = _a.open, _f = _a.onOpenChange, onOpenChange = _f === void 0 ? noop : _f, clearable = _a.clearable, _g = _a.placement, placement = _g === void 0 ? "bottom-start" : _g, _h = _a.placementOffset, placementOffset = _h === void 0 ? 5 : _h, _j = _a.closeOnScroll, closeOnScroll = _j === void 0 ? true : _j, dateRender = _a.dateRender, popoverProps = __rest(_a, ["header", "className", "style", "value", "onChange", "onInputValueChange", "disabled", "format", "placeholder", "defaultOpen", "open", "onOpenChange", "clearable", "placement", "placementOffset", "closeOnScroll", "dateRender"]);
    var _k = __read(useState(false), 2), hover = _k[0], setHover = _k[1];
    // 当前面板类型
    var _l = __read(useState("month"), 2), type = _l[0], setType = _l[1];
    // 当前面板展示时间
    var _m = __read(useState(moment.isMoment(value) ? value : getDefaultViewMoment()), 2), curViewMoment = _m[0], setCurViewMoment = _m[1];
    // 当前选中日期
    var _o = __read(useState(value), 2), curValue = _o[0], setCurValue = _o[1];
    // 选择器是否展开
    var _p = __read(useDefault(open, defaultOpen, onOpenChange), 2), active = _p[0], setActive = _p[1];
    useEffect(function () {
        setCurValue(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);
    // 输入框显示值
    var inputRef = useRef(null);
    var getInputValue = useCallback(function (value) {
        return moment.isMoment(value)
            ? value.locale(t.locale).format(format)
            : "";
    }, [t.locale, format]);
    var _q = __read(useState(getInputValue(curValue)), 2), inputValue = _q[0], setInputValue = _q[1];
    var syncInputValue = useCallback(function (value) {
        var inputValue = getInputValue(value);
        setInputValue(inputValue);
        onInputValueChange(inputValue, { valid: true });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getInputValue]);
    useEffect(function () {
        setCurValue(value);
        syncInputValue(value);
    }, [format, value, getInputValue, syncInputValue]);
    useEffect(function () {
        setCurViewMoment(moment.isMoment(curValue) ? curValue : getDefaultViewMoment());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curValue]);
    function getDefaultViewMoment() {
        var range = props.range;
        var m = moment();
        // range 判断
        if (Array.isArray(range)) {
            var _a = __read(range, 2), start = _a[0], end = _a[1];
            if (!moment.isMoment(start)) {
                start = moment(0);
            }
            if (!moment.isMoment(end)) {
                end = moment(Math.pow(2, 52));
            }
            if (isBefore(end, m, "year")) {
                return end;
            }
            if (isAfter(start, m, "year")) {
                return start;
            }
        }
        return m;
    }
    function handleChange(value, context) {
        setCurValue(value);
        syncInputValue(value);
        onChange(value, context);
        handleClose();
    }
    function handleOpen() {
        if (disabled) {
            return;
        }
        setActive(true);
        setType("month");
    }
    function handleClose() {
        syncInputValue(value);
        setActive(false);
    }
    function handleInputChange(content) {
        setInputValue(content);
        var value = moment(content, format, true);
        if (!value.isValid()) {
            onInputValueChange(content, { valid: false });
            return;
        }
        var valid = isValidMonth(value, props);
        if (valid) {
            setCurValue(value);
        }
        onInputValueChange(content, { valid: true });
    }
    function handleKeyDown(event) {
        if (!active) {
            handleOpen();
            return;
        }
        switch (event.key) {
            case KeyMap.Enter:
                onChange(curValue, { event: event });
                handleClose();
                break;
            case KeyMap.Esc:
                handleClose();
                break;
        }
    }
    return (React.createElement(Popover, __assign({ trigger: [
            DatePickerTrigger,
            { onOpen: handleOpen, onClose: handleClose },
        ], visible: active, onVisibleChange: setActive, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll }, popoverProps, { overlay: React.createElement(DropdownBox, { preventMouseDown: true },
            React.createElement(CalendarPart.Panel, null,
                !!header && React.createElement(CalendarPart.Header, null, header),
                React.createElement(CalendarTable, __assign({}, props, { monthOnly: true, value: curValue, onChange: handleChange, type: type, onTypeChange: setType, curViewMoment: curViewMoment, onCurViewMomentChange: setCurViewMoment, dateRender: dateRender })))) }),
        React.createElement("div", __assign({ ref: ref, className: classNames(classPrefix + "-monthpicker", className), style: style }, mergeEventProps(props)),
            React.createElement("div", { className: classNames(classPrefix + "-monthpicker__input", {
                    "is-hover": hover && value && clearable,
                }), onMouseEnter: function () { return setHover(true); }, onMouseLeave: function () { return setHover(false); } },
                React.createElement(Input, { ref: inputRef, disabled: disabled, placeholder: placeholder, value: inputValue, onChange: handleInputChange, onKeyDown: handleKeyDown, onClick: function (event) {
                        if (active) {
                            event.stopPropagation();
                        }
                    } }),
                clearable && !disabled && (React.createElement(Icon, { type: "dismiss", onClick: function (event) {
                        event.stopPropagation();
                        onChange(null, { event: event });
                    } })),
                React.createElement(CalendarIcon, null)))));
}, {
    defaultLabelAlign: "middle",
});
MonthPicker.displayName = "MonthPicker";
//# sourceMappingURL=MonthPicker.js.map